var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _SignedCallDataPack_instances, _SignedCallDataPack_encode;
import { BigNumber } from '@ethersproject/bignumber';
import { arrayify, hexlify } from '@ethersproject/bytes';
import * as cbor from 'cborg';
const DEFAULT_GAS_PRICE = 1; // Default gas params are assigned in the web3 gateway.
const DEFAULT_GAS_LIMIT = 30000000;
const DEFAULT_VALUE = 0;
const DEFAULT_DATA = '0x';
const zeroAddress = () => `0x${'0'.repeat(40)}`;
export function signedCallEIP712Params(chainId) {
    return {
        domain: {
            name: 'oasis-runtime-sdk/evm: signed query',
            version: '1.0.0',
            chainId,
        },
        types: {
            Call: [
                { name: 'from', type: 'address' },
                { name: 'to', type: 'address' },
                { name: 'gasLimit', type: 'uint64' },
                { name: 'gasPrice', type: 'uint256' },
                { name: 'value', type: 'uint256' },
                { name: 'data', type: 'bytes' },
                { name: 'leash', type: 'Leash' },
            ],
            Leash: [
                { name: 'nonce', type: 'uint64' },
                { name: 'blockNumber', type: 'uint64' },
                { name: 'blockHash', type: 'bytes32' },
                { name: 'blockRange', type: 'uint64' },
            ],
        },
    };
}
/**
 * Parameters that define a signed call that shall be
 * CBOR-encoded and sent as the call's `data` field.
 */
export class SignedCallDataPack {
    constructor(leash, 
    /** A signature over the call and leash as generated by `signCall`. */
    signature, 
    /**
     * An oasis-sdk `Call` without the optional fields.
     *
     * After encryption, `body` would be encrypted and this field would contain a
     * `format` field. The runtime would decode the data as a `types::transaction::Call`.
     **/
    data) {
        this.leash = leash;
        this.signature = signature;
        this.data = data;
        _SignedCallDataPack_instances.add(this);
    }
    static make(call, signer, overrides) {
        return __awaiter(this, void 0, void 0, function* () {
            const leash = yield makeLeash(signer, overrides === null || overrides === void 0 ? void 0 : overrides.leash);
            return new SignedCallDataPack(leash, yield signCall(makeSignableCall(call, leash), signer, {
                chainId: overrides === null || overrides === void 0 ? void 0 : overrides.chainId,
            }), call.data ? arrayify(call.data, { allowMissingPrefix: true }) : undefined);
        });
    }
    encode() {
        return __classPrivateFieldGet(this, _SignedCallDataPack_instances, "m", _SignedCallDataPack_encode).call(this, this.data ? { body: this.data } : undefined);
    }
    /** Encodes the data pack after encrypting the signed call data. */
    encryptEncode(cipher) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.data)
                return __classPrivateFieldGet(this, _SignedCallDataPack_instances, "m", _SignedCallDataPack_encode).call(this, yield cipher.encryptEnvelope(this.data));
            return this.encode();
        });
    }
}
_SignedCallDataPack_instances = new WeakSet(), _SignedCallDataPack_encode = function _SignedCallDataPack_encode(data) {
    return hexlify(cbor.encode({
        data: data ? data : undefined,
        leash: this.leash,
        signature: this.signature,
    }));
};
function makeLeash(signer, overrides) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const nonceP = (overrides === null || overrides === void 0 ? void 0 : overrides.nonce)
            ? overrides.nonce
            : signer.getTransactionCount('pending');
        let blockP;
        if ((overrides === null || overrides === void 0 ? void 0 : overrides.block) !== undefined) {
            blockP = overrides.block;
        }
        else {
            if (signer._checkProvider)
                signer._checkProvider('getBlock');
            const latestBlock = yield signer.provider.getBlock('latest');
            // The latest block is not historical and may not
            // even be the latest light client verifiable block.
            blockP = signer.provider.getBlock(latestBlock.number - 2);
        }
        const [nonce, block] = yield Promise.all([nonceP, blockP]);
        return {
            nonce,
            block_number: block.number,
            block_hash: arrayify(block.hash),
            block_range: (_a = overrides === null || overrides === void 0 ? void 0 : overrides.blockRange) !== null && _a !== void 0 ? _a : 15 /* ~90s */,
        };
    });
}
export function makeSignableCall(call, leash) {
    var _a, _b, _c, _d, _e;
    return {
        from: call.from,
        to: (_a = call.to) !== null && _a !== void 0 ? _a : zeroAddress(),
        gasLimit: BigNumber.from((_c = (_b = call.gas) !== null && _b !== void 0 ? _b : call.gasLimit) !== null && _c !== void 0 ? _c : DEFAULT_GAS_LIMIT).toNumber(),
        gasPrice: BigNumber.from((_d = call.gasPrice) !== null && _d !== void 0 ? _d : DEFAULT_GAS_PRICE),
        value: BigNumber.from((_e = call.value) !== null && _e !== void 0 ? _e : DEFAULT_VALUE),
        data: call.data
            ? hexlify(call.data, { allowMissingPrefix: true })
            : DEFAULT_DATA,
        leash: {
            nonce: leash.nonce,
            blockNumber: leash.block_number,
            blockHash: leash.block_hash,
            blockRange: leash.block_range,
        },
    };
}
function signCall(call, signer, overrides) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const chainId = (_a = overrides === null || overrides === void 0 ? void 0 : overrides.chainId) !== null && _a !== void 0 ? _a : (yield signer.getChainId());
        const { domain, types } = signedCallEIP712Params(chainId);
        return arrayify(yield signer._signTypedData(domain, types, call));
    });
}
//# sourceMappingURL=signed_calls.js.map